import { useHandler } from "@redotech/react-util/hook";
import BracketIcon from "@redotech/redo-icons/brackets-ellipses_filled.svg";
import ColorsIcon from "@redotech/redo-icons/colors.svg";
import EmojiIcon from "@redotech/redo-icons/face-smile.svg";
import LinkIcon from "@redotech/redo-icons/link-01_filled.svg";
import MinusIcon from "@redotech/redo-icons/minus.svg";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import TypeIcon from "@redotech/redo-icons/type-01_filled.svg";
import classNames from "classnames";
import {
  JSXElementConstructor,
  memo,
  MouseEvent,
  SVGProps,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { RedoButton } from "../arbiter-components/buttons/redo-button";
import { BaseRedoInputContainer } from "../arbiter-components/input/base-redo-input-container";
import { BaseRedoInput } from "../arbiter-components/input/base-redo-text-input";
import { Flex } from "../flex";
import AlignCenterIcon from "../icon-old/align-center.svg";
import AlignLeftIcon from "../icon-old/align-left.svg";
import AlignRightIcon from "../icon-old/align-right.svg";
import BoldIcon from "../icon-old/bold.svg";
import ItalicIcon from "../icon-old/italic.svg";
import ListIcon from "../icon-old/list.svg";
import StrikethroughIcon from "../icon-old/strikethrough.svg";
import UnderlineIcon from "../icon-old/underline.svg";
import * as richTextEditorCss from "./forge-quill-editor.module.css";
import {
  CollapsedActions,
  QuillToolbarItem,
} from "./quill-toolbar-collapsed-actions";

const DEFAULT_ITEM_WIDTH = 30;
const SPACE_FOR_OVERFLOW_BUTTON = 44;

/** Quill binds to RedoButton's real `<button>`; ids are toolbar-namespaced. */
function toolbarOption(
  toolbarId: string,
  name: string,
  buttonTitle: string,
  Icon: JSXElementConstructor<SVGProps<SVGSVGElement>>,
  buttonClass: string,
  value?: string,
): QuillToolbarItem {
  const buttonId = `${toolbarId}-${name}`;
  return {
    identifier: buttonId,
    buttonTitle,
    icon: Icon,
    element: (
      <div className={richTextEditorCss.textToolbarOption}>
        <Icon />
        <RedoButton
          aria-label={buttonTitle}
          buttonClassName={buttonClass}
          buttonId={buttonId}
          buttonValue={value}
          className={richTextEditorCss.toolbarOptionHitArea}
        />
      </div>
    ),
  };
}

export interface FontSize {
  label: string;
  value: string;
}

export const FormQuillEditorToolbar = memo(function FormQuillEditorToolbar({
  open,
  id = "toolbar",
  includeDynamicVariables = false,
  includeHeading = false,
  includeFontFamily = false,
  includeLink = true,
  includeEmoji = true,
  inline = false,
  fontSize,
  setFontSize,
  stepFontSize,
  anchorEl,
  anchorRect,
  usePortal = false,
}: {
  open: boolean;
  id?: string;
  includeDynamicVariables?: boolean;
  /** Per-line Body/Heading picker; opt-in since it needs page heading styles. */
  includeHeading?: boolean;
  /**
   * Per-selection font picker. Opt-in: only surfaces that render the resulting
   * inline `font-family` (and load the face) should offer it.
   */
  includeFontFamily?: boolean;
  /** Opt-out for surfaces whose content should not carry links. */
  includeLink?: boolean;
  /** Opt-out for surfaces where an emoji picker is noise. */
  includeEmoji?: boolean;
  /** In-flow with overflow collapse; off by default so un-anchored bars float. */
  inline?: boolean;
  /** `null` means the selection mixes sizes; the box shows blank, as in Docs. */
  fontSize: number | null;
  setFontSize: (fontSize: number) => void;
  /** Steps every run in the selection relative to its own size. */
  stepFontSize: (delta: 1 | -1) => void;
  /**
   * Floats the toolbar above this element, positioned imperatively on scroll
   * (no React state per event) so it tracks the anchor without lagging.
   */
  anchorEl?: HTMLElement | null;
  /** Legacy static-rect alternative to anchorEl (caller recomputes it itself). */
  anchorRect?: DOMRect | null;
  /** When true, always renders via portal to document.body (must stay consistent to preserve Quill bindings) */
  usePortal?: boolean;
}) {
  const toolbarRef = useRef<HTMLDivElement>(null);

  const [sizeDraft, setSizeDraftState] = useState<string | null>(null);
  // Escape blurs synchronously inside its keydown, before React re-renders,
  // so the blur commit must read the draft from a ref, not render state.
  const sizeDraftRef = useRef<string | null>(null);
  const setSizeDraft = useCallback((draft: string | null) => {
    sizeDraftRef.current = draft;
    setSizeDraftState(draft);
  }, []);
  const commitSizeDraft = useHandler(() => {
    const draft = sizeDraftRef.current;
    setSizeDraft(null);
    const size = Number(draft);
    if (draft !== null && size > 0 && size !== fontSize) {
      setFontSize(size);
    }
  });
  const stepWithoutStealingFocus = (e: MouseEvent<HTMLElement>) => {
    e.preventDefault();
    // A stale draft would otherwise commit on the box's blur and undo the step.
    setSizeDraft(null);
  };

  const floating = Boolean(anchorEl || anchorRect);

  // Never collapsed: these report the state the cursor is sitting on.
  const fixedControlsRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLDivElement>(null);

  const collapsibleItems = useMemo<QuillToolbarItem[]>(
    () => [
      toolbarOption(id, "bold", "Bold", BoldIcon, "ql-bold"),
      toolbarOption(id, "italic", "Italic", ItalicIcon, "ql-italic"),
      toolbarOption(
        id,
        "underline",
        "Underline",
        UnderlineIcon,
        "ql-underline",
      ),
      toolbarOption(
        id,
        "strike",
        "Strikethrough",
        StrikethroughIcon,
        "ql-strike",
      ),
      toolbarOption(
        id,
        "align-left",
        "Align left",
        AlignLeftIcon,
        "ql-align",
        "",
      ),
      toolbarOption(
        id,
        "align-center",
        "Align center",
        AlignCenterIcon,
        "ql-align",
        "center",
      ),
      toolbarOption(
        id,
        "align-right",
        "Align right",
        AlignRightIcon,
        "ql-align",
        "right",
      ),
      toolbarOption(id, "list", "Bulleted list", ListIcon, "ql-list", "bullet"),
      ...(includeLink
        ? [toolbarOption(id, "link", "Link", LinkIcon, "rql-link")]
        : []),
      toolbarOption(id, "color", "Text color", ColorsIcon, "rql-color"),
      ...(includeFontFamily
        ? [
            toolbarOption(
              id,
              "font-family",
              "Font",
              TypeIcon,
              "rql-font-family",
            ),
          ]
        : []),
      ...(includeDynamicVariables
        ? [
            toolbarOption(
              id,
              "variable",
              "Dynamic variable",
              BracketIcon,
              "rql-dynamic-variable",
            ),
          ]
        : []),
      ...(includeEmoji
        ? [toolbarOption(id, "emoji", "Emoji", EmojiIcon, "rql-emoji")]
        : []),
    ],
    [id, includeDynamicVariables, includeLink, includeEmoji, includeFontFamily],
  );

  const [collapsedIds, setCollapsedIds] = useState<ReadonlySet<string>>(
    new Set(),
  );
  const collapsedItems = useMemo(
    () => collapsibleItems.filter((item) => collapsedIds.has(item.identifier)),
    [collapsibleItems, collapsedIds],
  );

  const measure = useCallback(() => {
    const toolbarEl = toolbarRef.current;
    if (!toolbarEl || !inline) {
      setCollapsedIds((prev) => (prev.size === 0 ? prev : new Set()));
      return;
    }
    // The parent's width, not the toolbar's — overflow already inflated that.
    const available =
      toolbarEl.parentElement?.clientWidth ?? toolbarEl.clientWidth;
    let used =
      (fixedControlsRef.current?.offsetWidth ?? 0) +
      (headingRef.current?.offsetWidth ?? 0);
    const widths = collapsibleItems.map((item) => {
      const cell = toolbarEl
        .querySelector(`#${item.identifier}`)
        ?.closest(`.${richTextEditorCss.toolbarCell}`);
      return cell instanceof HTMLElement && cell.offsetWidth > 0
        ? cell.offsetWidth
        : DEFAULT_ITEM_WIDTH;
    });
    const next = new Set<string>();
    // Reserve the trigger's space only on real overflow, so an exact fit keeps
    // its last item.
    if (used + widths.reduce((sum, width) => sum + width, 0) > available) {
      let overflowing = false;
      for (const [index, item] of collapsibleItems.entries()) {
        const width = widths[index] ?? DEFAULT_ITEM_WIDTH;
        if (
          overflowing ||
          used + width > available - SPACE_FOR_OVERFLOW_BUTTON
        ) {
          overflowing = true;
          next.add(item.identifier);
          continue;
        }
        used += width;
      }
    }
    setCollapsedIds((prev) =>
      prev.size === next.size && [...prev].every((key) => next.has(key))
        ? prev
        : next,
    );
  }, [inline, collapsibleItems]);

  useLayoutEffect(() => {
    const toolbarEl = toolbarRef.current;
    if (!toolbarEl || !inline) {
      return;
    }
    measure();
    const animationFrame = requestAnimationFrame(measure);
    const observer = new ResizeObserver(measure);
    observer.observe(toolbarEl);
    if (toolbarEl.parentElement) {
      observer.observe(toolbarEl.parentElement);
    }
    return () => {
      cancelAnimationFrame(animationFrame);
      observer.disconnect();
    };
  }, [inline, measure]);

  useLayoutEffect(() => {
    if (!open || (!anchorEl && !anchorRect)) {
      return;
    }
    const position = () => {
      const toolbarEl = toolbarRef.current;
      if (!toolbarEl) {
        return;
      }
      const rect = anchorEl ? anchorEl.getBoundingClientRect() : anchorRect;
      if (!rect) {
        return;
      }
      toolbarEl.style.left = `${rect.left + rect.width / 2}px`;
      toolbarEl.style.top = `${rect.top}px`;
    };
    position();
    // A live element is tracked here; a static anchorRect is repositioned by
    // its owner re-passing the prop, which re-runs this effect.
    if (!anchorEl) {
      return;
    }
    // Capture phase so scrolls inside nested containers (the builder canvas)
    // reposition the toolbar too, not just window scrolls.
    window.addEventListener("scroll", position, true);
    window.addEventListener("resize", position);
    const resizeObserver = new ResizeObserver(position);
    resizeObserver.observe(anchorEl);
    return () => {
      window.removeEventListener("scroll", position, true);
      window.removeEventListener("resize", position);
      resizeObserver.disconnect();
    };
  }, [open, anchorEl, anchorRect]);

  // Portal/inline rendering must not switch or Quill's bindings break; left
  // and top are set imperatively by the layout effect above.
  const toolbar = (
    <Flex
      className={classNames(
        richTextEditorCss.textFormattingToolbar,
        inline && richTextEditorCss.textFormattingToolbarInline,
        !open && !inline && richTextEditorCss.textFormattingToolbarClosed,
      )}
      gap="none"
      id={id}
      maxW={inline ? "full" : undefined}
      onMouseDown={(e) => e.stopPropagation()}
      ref={toolbarRef}
      // design-allow: fixed geometry is paired with the imperative left/top
      style={{
        visibility: open ? "visible" : "hidden",
        ...(floating && {
          position: "fixed",
          transform: "translate(-50%, -120%)",
          zIndex: 1001,
        }),
      }}
    >
      {/* inline style works with Quill's magic much better than external CSS */}
      {/* The id="toolbar" is required for Quill to detect this toolbar */}
      <Flex
        align="center"
        borderColor="primary"
        borderRightWidth="1px"
        borderStyle="solid"
        gap="xs"
        // Tightened inline: a settings panel gives this bar only ~244px.
        px={inline ? "xs" : "md"}
        py="xs"
        ref={fixedControlsRef}
      >
        {/* mousedown preventDefault keeps the editor focused and its
            selection intact, like Quill's own toolbar buttons. */}
        <Flex
          align="center"
          aria-label="Decrease font size"
          className={richTextEditorCss.fontSizeButton}
          onClick={() => stepFontSize(-1)}
          onMouseDown={stepWithoutStealingFocus}
          role="button"
        >
          <MinusIcon height={16} width={16} />
        </Flex>
        {/* Remounting on open re-measures the input's width: a closed
            floating toolbar is display:none, so it measures as 0px wide. */}
        <BaseRedoInputContainer key={open ? "open" : "closed"}>
          {/* Typed sizes commit on Enter/blur: committing per keystroke would
              apply "3" on the way to "30" and pull focus back to the editor. */}
          <BaseRedoInput
            ariaLabel="Font size"
            constrainToInputLength
            htmlPattern="^[0-9]+$"
            onBlur={commitSizeDraft}
            onFocus={() => setSizeDraft(fontSize?.toString() ?? "")}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.currentTarget.blur();
              } else if (e.key === "Escape") {
                setSizeDraft(null);
                e.currentTarget.blur();
              }
            }}
            setValue={setSizeDraft}
            value={sizeDraft ?? fontSize?.toString() ?? ""}
          />
        </BaseRedoInputContainer>
        <Flex
          align="center"
          aria-label="Increase font size"
          className={richTextEditorCss.fontSizeButton}
          onClick={() => stepFontSize(1)}
          onMouseDown={stepWithoutStealingFocus}
          role="button"
        >
          <PlusIcon height={16} width={16} />
        </Flex>
      </Flex>
      {includeHeading && (
        <div ref={headingRef}>
          {/* design-allow: Quill's header picker binds to a native select */}
          <select
            className="ql-header"
            // Quill reads the picker's labels from each option's text, and
            // falls back to its own "Normal" unless one option carries the
            // `selected` attribute — which React will not render from a prop.
            ref={(el) =>
              el
                ?.querySelector("option[value='']")
                ?.setAttribute("selected", "")
            }
          >
            <option value="">Body</option>
            <option value="1">Heading 1</option>
            <option value="2">Heading 2</option>
            <option value="3">Heading 3</option>
          </select>
        </div>
      )}
      {collapsibleItems.map((item) => (
        <div
          className={classNames(
            richTextEditorCss.toolbarCell,
            // Hidden, not unmounted: Quill binds its handlers once at init.
            collapsedIds.has(item.identifier) &&
              richTextEditorCss.toolbarCellCollapsed,
          )}
          key={item.identifier}
        >
          {item.element}
        </div>
      ))}
      {collapsedItems.length > 0 && (
        // Not a toolbar cell, so it needs its own stretch/centering.
        <Flex
          align="center"
          alignSelf="stretch"
          className={richTextEditorCss.toolbarOverflow}
          px="xs"
        >
          <CollapsedActions collapsedItems={collapsedItems} toolbarId={id} />
        </Flex>
      )}
    </Flex>
  );

  return usePortal ? createPortal(toolbar, document.body) : toolbar;
});
